import { Spacing } from '@persona-id/design-tokens';
import { snakeCase, startCase } from 'lodash/fp';
import { Controller, FieldErrors, UseFormReturn } from 'react-hook-form';
import styled from 'styled-components';

import {
  MATCH_REQUIREMENT_TO_DESCRIPTION,
  MATCH_REQUIREMENT_TO_DISPLAY_NAME,
} from 'dashboard/components/ExtractionPanel/constants';
import {
  DEFAULT_AI_MODEL,
  ExtractionPanelForm,
  MatchRequirement,
} from 'dashboard/components/ExtractionPanel/util';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import {
  CompositeComparisonMethodType,
  DefinitionType,
  ExtractionFieldType,
  ExtractionRequest,
  ExtractionType,
  ExtractorType,
  GuidedExtractionRequest,
  GuidedPreset,
  MatchLevel,
} from 'dashboard/models/document';
import { TransactionTypeStatus } from 'dashboard/models/transaction-type';
import { ComparisonMethodType, NormalizationMethodType } from 'dashboard/models/verification-check';
import { useListTransactionTypesQuery } from 'dashboard/redux/api/transaction-type';
import { SelectField } from 'design-system/components/FormField';
import { Option } from 'design-system/components/Select';
import { FieldSchemaType } from 'lib/fields';
import { assertNever } from 'lib/utils/types';

const Section = styled.section`
  padding: 0 ${Spacing[4]} ${Spacing[4]} ${Spacing[4]};
`;

const SectionBreak = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const SelectLoadingIndicator = styled(LoadingIndicator)`
  padding: 0;
  height: 32px;
  width: 32px;
`;

export const extractionToTransactionGuidedExtractionFormState = (
  extractionRequest: GuidedExtractionRequest,
): Partial<ExtractionPanelForm> => {
  let transactionFieldName = undefined;
  if (extractionRequest.version === '3.0') {
    transactionFieldName = extractionRequest.subfields.map((s) => s['source-field'])[0];
  } else {
    transactionFieldName = extractionRequest['field-name'];
  }

  return {
    version: extractionRequest.version,
    fieldName: startCase(extractionRequest['field-name']),
    extractionType: extractionRequest['extraction-type'],
    extractorType: ExtractorType.TransactionFieldComparison,
    fieldType: extractionRequest['field-type'],
    transactionFieldName,
    matchRequirement:
      extractionRequest.preset === GuidedPreset.GenericStrict
        ? MatchRequirement.Strict
        : extractionRequest.preset === GuidedPreset.GenericLoose
          ? MatchRequirement.Loose
          : MatchRequirement.LooseAi,
  };
};

export const transactionGuidedExtractionFormStateToExtraction = (
  data: ExtractionPanelForm,
): ExtractionRequest => {
  if (data.version === '2.0') {
    return undefined as never;
  }

  switch (data.matchRequirement) {
    case MatchRequirement.Loose:
      return {
        version: '3.0',
        'extraction-type': ExtractionType.Guided,
        'field-type': ExtractionFieldType.String,
        'field-name': snakeCase(data.fieldName),
        'definition-type': DefinitionType.Preset,
        preset: GuidedPreset.GenericLoose,
        subfields: [
          {
            'source-field': data.transactionFieldName,
            'search-method': { method: 'full_string' },
            criteria: [
              {
                'match-level': MatchLevel.Partial,
                'logic-composition': {
                  type: 'logic_composition',
                  'match-level-minimum': MatchLevel.Partial,
                  operator: 'comparison',
                  'comparison-method': {
                    type: ComparisonMethodType.StringSimilarity,
                    'full-match-threshold': 0.9,
                    'partial-match-threshold': 0.7,
                    'max-char-difference': 4,
                    'match-level-minimum': MatchLevel.Partial,
                  },
                  'normalization-methods': [
                    { type: NormalizationMethodType.FoldCharacters },
                    { type: NormalizationMethodType.LowercaseLetters },
                  ],
                  'augmentation-methods': [],
                },
              },
            ],
          },
        ],
        'match-criteria': [
          {
            'match-level': MatchLevel.Partial,
            'subfields-composite-comparison-method': {
              type: CompositeComparisonMethodType.LogicComposition,
              operator: 'and',
              components: [
                {
                  type: CompositeComparisonMethodType.LogicComposition,
                  operator: 'comparison',
                  components: [],
                  'comparison-method': {
                    type: CompositeComparisonMethodType.SubfieldMatchFound,
                    'match-level-minimum': MatchLevel.Partial,
                    subfield: data.transactionFieldName,
                  },
                },
              ],
            },
          },
        ],
      };
    case MatchRequirement.LooseAi:
      return {
        version: '3.0',
        'extraction-type': ExtractionType.Guided,
        'field-type': ExtractionFieldType.String,
        'field-name': snakeCase(data.fieldName),
        'definition-type': DefinitionType.Preset,
        preset: GuidedPreset.GenericLooseAi,
        subfields: [
          {
            'source-field': data.transactionFieldName,
            'search-method': { method: 'full_string' },
            criteria: [
              {
                'match-level': MatchLevel.Partial,
                'logic-composition': {
                  type: 'logic_composition',
                  'match-level-minimum': MatchLevel.Partial,
                  operator: 'comparison',
                  'comparison-method': {
                    type: ComparisonMethodType.Ai,
                    model: DEFAULT_AI_MODEL,
                    'match-level-minimum': MatchLevel.Partial,
                  },
                  'normalization-methods': [],
                  'augmentation-methods': [],
                },
              },
            ],
            'ai-model': DEFAULT_AI_MODEL,
          },
        ],
        'match-criteria': [
          {
            'match-level': MatchLevel.Partial,
            'subfields-composite-comparison-method': {
              type: CompositeComparisonMethodType.LogicComposition,
              operator: 'and',
              components: [
                {
                  type: CompositeComparisonMethodType.LogicComposition,
                  operator: 'comparison',
                  components: [],
                  'comparison-method': {
                    type: CompositeComparisonMethodType.SubfieldMatchFound,
                    'match-level-minimum': MatchLevel.Partial,
                    subfield: data.transactionFieldName,
                  },
                },
              ],
            },
          },
        ],
      };
    case MatchRequirement.Strict:
      return {
        version: '3.0',
        'extraction-type': ExtractionType.Guided,
        'field-type': ExtractionFieldType.String,
        'field-name': snakeCase(data.fieldName),
        'definition-type': DefinitionType.Preset,
        preset: GuidedPreset.GenericStrict,
        subfields: [
          {
            'source-field': data.transactionFieldName,
            'search-method': { method: 'full_string' },
            criteria: [
              {
                'match-level': MatchLevel.Partial,
                'logic-composition': {
                  type: 'logic_composition',
                  'match-level-minimum': MatchLevel.Partial,
                  operator: 'comparison',
                  'comparison-method': {
                    type: ComparisonMethodType.StringSimilarity,
                    'full-match-threshold': 1.0,
                    'partial-match-threshold': 1.0,
                    'max-char-difference': 4,
                    'match-level-minimum': MatchLevel.Partial,
                  },
                  'normalization-methods': [{ type: NormalizationMethodType.LowercaseLetters }],
                  'augmentation-methods': [],
                },
              },
              {
                'match-level': MatchLevel.Full,
                'logic-composition': {
                  type: 'logic_composition',
                  'match-level-minimum': MatchLevel.Full,
                  operator: 'comparison',
                  'comparison-method': {
                    type: ComparisonMethodType.StringSimilarity,
                    'full-match-threshold': 1.0,
                    'partial-match-threshold': 1.0,
                    'max-char-difference': 4,
                    'match-level-minimum': MatchLevel.Partial,
                  },
                  'normalization-methods': [{ type: NormalizationMethodType.LowercaseLetters }],
                  'augmentation-methods': [],
                },
              },
            ],
          },
        ],
        'match-criteria': [
          {
            'match-level': MatchLevel.Partial,
            'subfields-composite-comparison-method': {
              type: CompositeComparisonMethodType.LogicComposition,
              operator: 'comparison',
              components: [],
              'comparison-method': {
                type: CompositeComparisonMethodType.SubfieldMatchFound,
                'match-level-minimum': MatchLevel.Partial,
                subfield: data.transactionFieldName,
              },
            },
          },
          {
            'match-level': MatchLevel.Full,
            'subfields-composite-comparison-method': {
              type: CompositeComparisonMethodType.LogicComposition,
              operator: 'comparison',
              components: [],
              'comparison-method': {
                type: CompositeComparisonMethodType.SubfieldMatchFound,
                'match-level-minimum': MatchLevel.Full,
                subfield: data.transactionFieldName,
              },
            },
          },
        ],
      };
    default:
      assertNever(data.matchRequirement as never);
  }
};

export const validateTransactionGuidedExtractionFormState = (data: ExtractionPanelForm) => {
  if (data.version === '2.0') {
    return undefined as never;
  }

  const REQUIRED_ERROR = { type: 'required', message: 'Required' };
  const errors: FieldErrors<ExtractionPanelForm> = {};

  if (data.transactionFieldName == null || data.transactionFieldName === '') {
    errors.transactionFieldName = REQUIRED_ERROR;
  }
  if (data.matchRequirement == null) {
    errors.matchRequirement = REQUIRED_ERROR;
  }

  return errors;
};

interface TransactionGuidedExtractionPanelProps {
  formReturn: UseFormReturn<ExtractionPanelForm, object>;
  disabled?: boolean;
}

const TransactionGuidedExtractionPanel = ({
  formReturn,
  disabled = false,
}: TransactionGuidedExtractionPanelProps) => {
  const { formState, watch, setValue, control } = formReturn;
  const { errors } = formState;

  const transactionTypeId = watch('transactionTypeId');

  const { isLoading: isLoadingTransactionTypes, data: { data: transactionTypes = [] } = {} } =
    useListTransactionTypesQuery({ filter: { status: [TransactionTypeStatus.Active] } });

  return (
    <>
      <Section>
        <Controller
          name='transactionTypeId'
          control={control}
          render={({ field }) => (
            <SelectField
              {...field}
              onChange={(e) => {
                field.onChange(e);
                setValue('transactionFieldName', '');
              }}
              label='Transaction type'
              placeholder='Select transaction type'
              errorText={errors.transactionTypeId?.message}
              disabled={disabled}
            >
              {Object.values(transactionTypes).map((transactionType) => (
                <Option key={transactionType.id} value={transactionType.id}>
                  {transactionType.attributes.name}
                </Option>
              ))}
            </SelectField>
          )}
        />
        <Controller
          name='transactionFieldName'
          control={control}
          render={({ field }) => {
            let transactionFields = Object.values(
              transactionTypes.find((tt) => tt.id === transactionTypeId)?.attributes[
                'field-schemas'
              ] || [],
            )
              .filter((fieldSchema) => fieldSchema.type === FieldSchemaType.String)
              .map((fieldSchema) => ({ key: fieldSchema.key, label: fieldSchema.label }));

            if (!transactionTypeId && !!field.value) {
              transactionFields = [{ key: field.value, label: startCase(field.value) }];
            }

            return (
              <SelectField
                {...field}
                label='Transaction field for comparison'
                placeholder='Select transaction field'
                errorText={errors.transactionFieldName?.message}
                disabled={disabled || (!transactionTypeId && !field.value)}
              >
                {isLoadingTransactionTypes ? (
                  <SelectLoadingIndicator />
                ) : (
                  transactionFields.map(({ key, label }) => (
                    <Option key={key} value={key}>
                      {label}
                    </Option>
                  ))
                )}
              </SelectField>
            );
          }}
        />
        <Controller
          name='matchRequirement'
          control={control}
          render={({ field }) => (
            <SelectField
              {...field}
              label='Match requirement'
              placeholder='Select match requirement'
              errorText={errors.matchRequirement?.message}
              disabled={disabled}
            >
              {Object.values(MatchRequirement).map((matchRequirement) => (
                <Option
                  key={matchRequirement}
                  value={matchRequirement}
                  subtitle={MATCH_REQUIREMENT_TO_DESCRIPTION[matchRequirement]}
                >
                  {MATCH_REQUIREMENT_TO_DISPLAY_NAME[matchRequirement]}
                </Option>
              ))}
            </SelectField>
          )}
        />
      </Section>
      <SectionBreak />
    </>
  );
};

export default TransactionGuidedExtractionPanel;
